import { chunkOfId } from '@versesverse/core';
import type { VerseChunkArtifact } from '@versesverse/schema/artifacts';
import type { TextOf } from './passages';

/*
 * READING A LOADED VERSE'S TEXT — the one place three hooks agree on how.
 *
 * `useNodeVerses`, `useContextVerses` and `useStoryPassages` each fetch a different SET of verses (a
 * figure's, a passage's neighbours, a shared episode's two sides), but once the ranges are in hand
 * they read a single verse out of them identically: find the chunk that holds the id, take its
 * English, original, second translation and mention list. Written once here so the four fields never
 * drift between them — the exact bug (`alt` and `mentions` silently dropped) this project keeps
 * finding in its own copies.
 */
export function makeTextOf(chunks: Map<number, VerseChunkArtifact>, chunkStarts: number[]): TextOf {
  return (id) => {
    const chunk = chunks.get(chunkOfId(chunkStarts, id));
    if (!chunk) return undefined;
    /*
     * The fourth English and beyond, turned from "one map per translation" into "this verse's
     * renderings". The chunk stores it the other way round — translation id first — because a
     * chunk holds thousands of verses and only a handful of translations, and repeating the
     * translation id on every verse would be most of the file.
     *
     * Absent for every chunk written before this existed, and for every text that has only the
     * three named slots, so the loop usually runs zero times.
     */
    let texts: Record<string, string> | undefined;
    for (const [translation, byVerse] of Object.entries(chunk.texts ?? {})) {
      const body = byVerse[String(id)];
      if (body) (texts ??= {})[translation] = body;
    }
    return {
      eng: chunk.eng[String(id)],
      orig: chunk.orig[String(id)],
      alt: chunk.alt?.[String(id)],
      alt2: chunk.alt2?.[String(id)],
      prov: chunk.prov?.[String(id)],
      mentions: chunk.mentions?.[String(id)],
      texts,
    };
  };
}
